import { type SelectHTMLAttributes } from "react";

import { cn } from "@/lib/utils";

type NativeSelectOption = {
  value: string;
  label: string;
};

type NativeSelectProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "value" | "onChange"
> & {
  value: string | undefined;
  onValueChange: (value: string) => void;
  items: NativeSelectOption[];
  placeholder?: string;
};

/**
 * NativeSelect — A native <select> styled to match the react-ui Select trigger.
 *
 * Uses a native element to avoid Radix DismissableLayer conflicts when
 * rendered alongside other Radix-based components (Autocomplete, Dialog, etc.).
 */
export const NativeSelect = ({
  value,
  onValueChange,
  items,
  placeholder = "Select",
  className,
  ...props
}: NativeSelectProps) => {
  return (
    <div className="relative">
      <select
        value={value ?? ""}
        onChange={(e) => onValueChange(e.target.value)}
        className={cn(
          "flex h-10 w-full appearance-none rounded-sm border border-border-input bg-input px-3 py-2 pr-8 text-sm text-text-input ring-offset-accent-dark-1",
          "placeholder:font-light placeholder:text-placeholder",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          !value && "text-placeholder font-light",
          className,
        )}
        {...props}
      >
        <option value="" disabled hidden>
          {placeholder}
        </option>
        {items.map((item) => (
          <option key={item.value} value={item.value} className="text-black">
            {item.label}
          </option>
        ))}
      </select>

      {/* Chevron icon matching react-ui Select */}
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-4 w-4 opacity-50"
        >
          <path
            fillRule="evenodd"
            d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    </div>
  );
};
